import { THEME, ThemeProvider } from '@vodacomsa/ns-react-components';
import {
  Form,
  Formik
} from 'formik';
import React, {
  useEffect,
  useMemo
} from 'react';
import { formFields } from './BusinessDirectorConstants';
import {
  directorValidationSchema,
  validateDirectorIdNumber,
  isPartialDirector
} from './directorValidationSchema';
import { useLocation, useOutletContext } from 'react-router-dom';
import { DirectorDetails } from '../../../constants';
import {
  useRecoilCallback,
  useRecoilValue
} from 'recoil';
import { FormBuilder } from '../../../components/FormBuilder';
import {
  ErrorModalSection,
  FormSubmitHelper,
  SupportPopup
} from '../../../components';
import {
  CommonHeight,
  FormContainer,
  FormField
} from '../../../constants';
import {
  mapValues,
  replaceUndefined,
  stripEmpty
} from '../../../helpers';
import {
  useIsMobile,
  directorDetailsSubmit,
  businessDetailsSubmit
} from '../../../recoil';

import { useModal } from '../../../components/modalController/useModal';
import { findFirstValidationError } from '../../../helpers/formik';
import { useTealium } from '../../../tealium/useTealium';
import { ApplicationRoutes } from '../../../Router';

export const BusinessDirectorDetails = () => {

  const [openModal, closeModal] = useModal();
  const { submitState, setSubmitState, backClick, submissionStates, nextClick } = useOutletContext();
  const businessDetails = useRecoilValue(businessDetailsSubmit);
  const businessIdToFetchDirector = (localStorage.getItem('isNewBusiness') === 'true') && businessDetails?.sharedBusinessDirectors ? businessDetails?.sharedBusinessDirectors : businessDetails?.businessId;
  const directorDetails = useRecoilValue(directorDetailsSubmit(businessIdToFetchDirector));

  let fetchedTotalDirectors = directorDetails?.directors?.length;
  const { track, events } = useTealium();
  const location = useLocation();
  const isMobile = useRecoilValue(useIsMobile);
  const isBusinessVerified = !(businessDetails?.isBusinessVerified === false);
  const isSoleTrader = businessDetails?.businessType === 'Sole Proprietorship';
  let formTouched = false;
  const handleFormFocus = () => {
    if (formTouched) {
      return;
    }

    formTouched = true;
    track(events.businessDirectors.formStart());
  };

  useEffect(() => {
    track(events.businessDirectors.pageView());
  }, []);

  const detailsPromise = useRecoilCallback(({ snapshot, set }) =>
    async (data, businessIdToFetchDirector) => {
      const selector = directorDetailsSubmit(businessIdToFetchDirector);
      set(selector, data);
      return await snapshot.getPromise(selector);
    }, []);

  const maskIdNumber = (str) => {
    const unmaskedCharLength = 6;
    const visible = str.slice(0, unmaskedCharLength);
    const masked = '#'.repeat(Math.max(0, str.length - unmaskedCharLength));
    return visible + masked;
  };

  const formDetails = useMemo(() => {

    const directors =
      directorDetails?.directors?.length === 0
        ? [{ ...DirectorDetails }]
        : directorDetails?.directors?.map((v) => {
          return { ...v, isIdVerified: true, maskedIdNumber: maskIdNumber(v.idNumber) };
        });

    const removeEmpty = replaceUndefined({
      ...directorDetails,
      directors
    });

    return removeEmpty;

  }, [directorDetails]);

  // TODO: To be removed after verification of director details save button implementation
  // const submitHandler = (formData) => {
  //   sessionStorage.removeItem('sharedBusinessDirector');
  //   sessionStorage.removeItem('sharedDirectors');
  //   if (isSoleTrader) {
  //     setSubmitState(submissionStates.submitSuccess);
  //     return;
  //   }
  //   detailsPromise(mapValues(stripEmpty({ ...formData, isBusinessVerified: false })));
  // };

  const submitDetails = async (formData) => {
    const filteredData = formData?.directors?.filter(d => !isPartialDirector(d));

    let directorFormData = {
      directors: filteredData.map(director => ({
        ...director,
        isDeleted: false
      }))
    };

    const data = await detailsPromise(mapValues(stripEmpty({ ...directorFormData, isBusinessVerified: false })),businessIdToFetchDirector);

    const directors =
      data?.directors?.length === 0
        ? [{ ...data }]
        : data?.directors?.map((v) => {
          return { ...v, isIdVerified: true, maskedIdNumber: maskIdNumber(v.idNumber) };
        });

    const updatedDirectors = replaceUndefined({
      directors
    });

    return updatedDirectors;
  };

  const closeErrorModal = () => {
    setSubmitState(submissionStates.default);
    closeModal();
  };

  useEffect(() => {
    sessionStorage.removeItem('regNumber');
    sessionStorage.removeItem('businessName');
    sessionStorage.removeItem('vatNumber');
  }, []);

  useEffect(() => {
    if (directorDetails?.error) {

      openModal({
        title: 'Error',
        children: <ErrorModalSection
          traceId={directorDetails?.traceId}
          error={directorDetails?.error}
          onCloseErrorModal={closeErrorModal}
        />,
        type: 'error',
        onCloseModal: closeErrorModal
      });
      return;
    }
    if (submitState === submissionStates.submitting && !directorDetails?.error) {
      setSubmitState(submissionStates.submitSuccess);
    }
  }, [directorDetails]);

  const handleFormikTouchedErrors = (touched, errors) => {
    const errorMessage = findFirstValidationError(errors, touched);

    if (!errorMessage) {
      return;
    }

    track(events.error('form', location.pathname, errorMessage));
  };

  const onBackClick = () => {
    track(events.businessDirectors.formEnd('back'));
    window.location.pathname = ApplicationRoutes.businessDetails;
    backClick();
  };

  const onNextClick = async () => {
    track(events.businessDirectors.formEnd('next'));
    const sharedBusinessId = businessDetails?.sharedBusinessDirectors;
    localStorage.removeItem('isNewBusiness');
    if (sharedBusinessId) {
      await submitDetails(formDetails);
    }
    setSubmitState(submissionStates.submitSuccess);
    return nextClick;
  };

  return (

    <div style={styles.container}>

      <ThemeProvider theme={THEME.revamp}>
        <Formik
          initialValues={formDetails}
          onSubmit={() => { }}
          validationSchema={directorValidationSchema}
          validateOnMount={true}
          validate={validateDirectorIdNumber}
          enableReinitialize={true}
        >
          {({ values, errors, touched }) => {
            handleFormikTouchedErrors(touched, errors);

            return (
              <Form style={FormContainer} onFocus={handleFormFocus}>
                <FormSubmitHelper submitState={submitState} setSubmitState={setSubmitState} />

                <FormBuilder
                  fields={formFields}
                  title="Director’s details"
                  subtitle='We need the company director details to process your business funding application.'
                  isMobile={isMobile}
                  values={values}
                  FormField={FormField}
                  errors={errors}
                  submitState={submitState}
                  setSubmitState={setSubmitState}
                  fetchedTotalDirectors={fetchedTotalDirectors}
                  backClick={onBackClick}
                  journeyType={'onBoarding'}
                  requireFooter={true}
                  isDetailsCaptured={directorDetails?.directors?.length > 0}
                  isBusinessVerified={isBusinessVerified}
                  isSoleTrader={isSoleTrader}
                  submissionStates={submissionStates}
                  nextClick={onNextClick}
                  touched={touched}
                  submitDirector={submitDetails}
                  sharedBusinessIdDirector={businessDetails?.sharedBusinessDirectors}
                />

              </Form>
            );
          }}
        </Formik>
      </ThemeProvider>
      <SupportPopup />
    </div>
  );
};

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    minHeight: '50vh',
    margin: `0 0 ${CommonHeight.NavigationFooterHeightDesktop} 0`
  }
};
